import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { dataTableSx, HeadLabelCell } from '../../../shared/components/TableHeader';
import {
  EMPTY_VALUE,
  formatEf,
  formatNumber,
  formatTonne,
} from '../../../shared/utils/number';
import { TOKENS, tableTokens } from '../../../theme/muiTheme';
import { DATA_SOURCE_IDS } from '../model/dataInputModel';

const EF_DISPLAY_DIGITS = 4;
const RESULT_TABLE_MAX_HEIGHT = 420;

const roundEf = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return number.toFixed(EF_DISPLAY_DIGITS);
};

const rowLabel = (row, t) => row.label || (row.labelKey ? t(row.labelKey) : row.id);

const AF_ROW_ID_PATTERN = /^CAT_(\d+)(?:_(\d+))?(?:\.[A-Z_]+)?$/;

const afCategoryCode = (rowId) => {
  const match = AF_ROW_ID_PATTERN.exec(rowId || '');
  if (!match) return null;
  const [, major, minor] = match;
  return minor ? `Cat ${major}.${minor}` : `Cat ${major}`;
};

function EfCell({ efValue, efUnit, showUnit = false }) {
  if (efValue === null || efValue === '') return <>{EMPTY_VALUE}</>;
  const rounded = roundEf(efValue);
  const fullValue = `${efValue}${efUnit ? ` ${efUnit}` : ''}`;
  return (
    <Tooltip title={fullValue}>
      <span>
        {formatEf(rounded)}
        {showUnit && efUnit && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            {efUnit}
          </Typography>
        )}
      </span>
    </Tooltip>
  );
}

function ActivityCell({ activity, activityUnit, showUnit = false }) {
  if (activity === null || activity === '') return <>{EMPTY_VALUE}</>;
  return (
    <span>
      {formatNumber(activity, { min: 0, max: 2 })}
      {showUnit && activityUnit && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          {activityUnit}
        </Typography>
      )}
    </span>
  );
}

const STATUS_STYLES = {
  CALCULATED: {
    labelKey: 'dataInput.resultTable.status.calculated',
    rowBg: theme => alpha(TOKENS.success, theme.palette.mode === 'dark' ? 0.16 : 0.08),
    textColor: 'text.primary',
  },
  NOT_CALCULATED: {
    labelKey: 'dataInput.resultTable.status.notCalculated',
    rowBg: theme => alpha(TOKENS.warning, theme.palette.mode === 'dark' ? 0.18 : 0.1),
    textColor: 'text.primary',
  },
  NO_DATA: {
    labelKey: 'dataInput.resultTable.status.noData',
    rowBg: theme => alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.08 : 0.04),
    textColor: 'text.secondary',
  },
};

function StatusCell({ status, rawRecords }) {
  const { t } = useTranslation();
  const style = STATUS_STYLES[status];
  if (!style) return <>{EMPTY_VALUE}</>;
  return (
    <span>
      <Typography
        variant="body2"
        sx={{ color: style.textColor, fontWeight: 600, whiteSpace: 'nowrap' }}
      >
        {t(style.labelKey)}
      </Typography>
      {status === 'NOT_CALCULATED' && rawRecords !== null && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t('dataInput.resultTable.status.rawRows', { count: rawRecords })}
        </Typography>
      )}
    </span>
  );
}

function ResultTableHead({ sourceId }) {
  const { t } = useTranslation();
  const column = key => t(`dataInput.resultTable.column.${key}`);

  if (sourceId === DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY) {
    return (
      <TableRow>
        <HeadLabelCell>{column('office')}</HeadLabelCell>
        <HeadLabelCell>{column('legalEntity')}</HeadLabelCell>
        <HeadLabelCell align="right">{column('electricity')}</HeadLabelCell>
        <HeadLabelCell align="right">{column('factorScope2')}</HeadLabelCell>
        <HeadLabelCell align="right">{column('emissions')}</HeadLabelCell>
      </TableRow>
    );
  }
  if (sourceId === DATA_SOURCE_IDS.SCOPE_3_AF_REPORT) {
    return (
      <TableRow>
        <HeadLabelCell>{column('category')}</HeadLabelCell>
        <HeadLabelCell align="right">{column('activity')}</HeadLabelCell>
        <HeadLabelCell align="right">{column('factor')}</HeadLabelCell>
        <HeadLabelCell align="right">{column('emissions')}</HeadLabelCell>
        <HeadLabelCell>{column('status')}</HeadLabelCell>
      </TableRow>
    );
  }
  if (sourceId === DATA_SOURCE_IDS.SCOPE_3_EWASTE) {
    return (
      <TableRow>
        <HeadLabelCell>{column('source')}</HeadLabelCell>
        <HeadLabelCell align="right">{column('waste')}</HeadLabelCell>
        <HeadLabelCell align="right">{column('factorEwaste')}</HeadLabelCell>
        <HeadLabelCell align="right">{column('emissions')}</HeadLabelCell>
      </TableRow>
    );
  }
  return (
    <TableRow>
      <HeadLabelCell>{column('company')}</HeadLabelCell>
      <HeadLabelCell align="right">{column('volume')}</HeadLabelCell>
      <HeadLabelCell align="right">{column('factorScope1')}</HeadLabelCell>
      <HeadLabelCell align="right">{column('emissions')}</HeadLabelCell>
    </TableRow>
  );
}

function ResultTableRow({ sourceId, row }) {
  const { t } = useTranslation();
  const isAf = sourceId === DATA_SOURCE_IDS.SCOPE_3_AF_REPORT;
  const isRollup = isAf && row.level === 0 && row.status === 'CALCULATED';
  const statusStyle = isAf ? STATUS_STYLES[row.status] : null;
  const categoryCode = isAf ? afCategoryCode(row.id) : null;

  const rowSx = {
    '& td': {
      color: statusStyle ? statusStyle.textColor : undefined,
    },
  };
  if (statusStyle?.rowBg) {
    rowSx.bgcolor = statusStyle.rowBg;
  }

  return (
    <TableRow sx={rowSx}>
      <TableCell
        sx={{
          fontWeight: isRollup ? 700 : 500,
          pl: isAf && row.level === 1 ? 4 : undefined,
          color: statusStyle ? statusStyle.textColor : undefined,
        }}
      >
        {categoryCode ? `${categoryCode} · ${rowLabel(row, t)}` : rowLabel(row, t)}
      </TableCell>
      {sourceId === DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY && (
        <TableCell sx={{ color: statusStyle ? statusStyle.textColor : undefined }}>
          {row.legalEntity || EMPTY_VALUE}
        </TableCell>
      )}
      <TableCell align="right" sx={{ color: statusStyle ? statusStyle.textColor : undefined }}>
        <ActivityCell
          activity={row.activity}
          activityUnit={row.activityUnit}
          showUnit={isAf}
        />
      </TableCell>
      <TableCell align="right" sx={{ color: statusStyle ? statusStyle.textColor : undefined }}>
        <EfCell efValue={row.efValue} efUnit={row.efUnit} showUnit={isAf} />
      </TableCell>
      <TableCell align="right" sx={{ color: statusStyle ? statusStyle.textColor : undefined }}>
        {row.emissions === null || row.emissions === '' ? EMPTY_VALUE : formatTonne(row.emissions)}
      </TableCell>
      {isAf && (
        <TableCell sx={{ color: statusStyle ? statusStyle.textColor : undefined }}>
          <StatusCell status={row.status} rawRecords={row.rawRecords} />
        </TableCell>
      )}
    </TableRow>
  );
}

function ResultTotalRow({ sourceId, total }) {
  const { t } = useTranslation();
  const isAf = sourceId === DATA_SOURCE_IDS.SCOPE_3_AF_REPORT;
  const isElectricity = sourceId === DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY;
  const totalSx = (theme) => ({
    '& td': {
      position: 'sticky',
      bottom: 0,
      zIndex: 1,
      bgcolor: tableTokens(theme).headBg,
      borderTop: `1px solid ${tableTokens(theme).headRule}`,
      fontWeight: 700,
      py: 1.25,
    },
  });

  return (
    <TableRow sx={totalSx}>
      <TableCell>{t('scopeEmissions.common.total')}</TableCell>
      {isElectricity && <TableCell />}
      <TableCell align="right">
        {total.activity === null || total.activity === ''
          ? EMPTY_VALUE
          : formatNumber(total.activity, { min: 0, max: 2 })}
      </TableCell>
      <TableCell align="right">{EMPTY_VALUE}</TableCell>
      <TableCell align="right">
        {total.emissions === null || total.emissions === ''
          ? EMPTY_VALUE
          : formatTonne(total.emissions)}
      </TableCell>
      {isAf && <TableCell />}
    </TableRow>
  );
}

export default function ResultSummaryTable({
  sourceId,
  data,
  loading,
  error,
  onRetry,
}) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1.25}
        sx={{ py: 3 }}
      >
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          {t('dataInput.resultTable.loading')}
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={(
          <Button color="inherit" size="small" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        )}
      >
        {t('dataInput.resultTable.loadError')}
      </Alert>
    );
  }

  const rows = data?.rows || [];
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        {t('dataInput.resultTable.empty')}
      </Typography>
    );
  }

  return (
    <TableContainer
      sx={{
        maxHeight: RESULT_TABLE_MAX_HEIGHT,
        overflowX: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Table
        stickyHeader
        size="small"
        sx={(theme) => ({
          ...dataTableSx(theme),
          minWidth: 560,
          '& thead th': {
            ...dataTableSx(theme)['& thead th'],
            position: 'sticky',
            top: 0,
            zIndex: 2,
          },
        })}
      >
        <TableHead>
          <ResultTableHead sourceId={sourceId} />
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <ResultTableRow key={row.key} sourceId={sourceId} row={row} />
          ))}
          <ResultTotalRow sourceId={sourceId} total={data.total} />
        </TableBody>
      </Table>
    </TableContainer>
  );
}